import { select } from 'd3-selection';

import { renderConfig } from '../constants';
import { getCanvasDimensions } from './config';
import { fontMetrics } from './fonts';
import { LabelPaddings } from './types';

// Compute padding values for label highlights, etc.
export function getLabelPaddings(formValues: SankeyFormValues): LabelPaddings {
  // Checks for Firefox-ness of the browser.
  // Why? Because we have to adjust SVG font spacing for Firefox's sake.
  // It would be better if SVG-font-sizing differences were detectable
  // directly, but so far I haven't figured out how to test for just
  // that, so we check for Firefox. (Many use 'InstallTrigger' to
  // check for FF, but that's been deprecated.)
  const isFirefox =
    navigator && /firefox/i.test(navigator.userAgent || navigator.vendor || '');
  // First, how big are an em and an ex in the current font, roughly?
  // The height is the same for all characters.
  const canvas = getCanvasDimensions(formValues);
  const fontSize = formValues.font_size ?? 14;
  const { width: emW, height: boundingBoxH } = measureText(
    canvas,
    fontSize,
    'm'
  );
  // The WIDTH of an 'x' is a crude estimate of the x-HEIGHT, but it's what we have for now.
  const { width: exH } = measureText(canvas, fontSize, 'x');
  // Firefox has unique SVG measurements in 2022, so we look for it.
  const metrics = fontMetrics[isFirefox ? 'firefox' : '*']['*'];
  const m = {
    dy: metrics.dy * boundingBoxH,
    top: metrics.top * exH,
    bot: metrics.bot * exH,
    inner: metrics.inner * emW,
    outer: metrics.outer * emW,
    lblMarginRight: 0,
    lblMarginLeft: 0,
  };
  // Compute the remaining values (which depend on values above).
  // lblMarginRight = total margin to give a label when it is to the right
  //   of a node. (Note: this value basically includes m.inner)
  // lblMarginLeft = total margin when label is to the left
  m.lblMarginRight = metrics.marginRight * m.inner + 1;
  m.lblMarginLeft = (metrics.marginRight + metrics.marginAdjLeft) * m.inner + 1;
  return m;
}

export function measureText(
  canvas: { width: number; height: number },
  fontSize: number,
  text: string
) {
  // To measure text sizes, first we make a dummy SVG area the user won't
  // see, with the same size and font details as the real diagram.
  // NOTE: d3.create('svg') on the fly without attaching it anywhere won't work unfortunately :(
  const scratchSVG = select('#svg_scratch')
    .attr('height', canvas.height)
    .attr('width', canvas.width)
    .attr('font-size', `${fontSize}px`)
    .attr('text-anchor', 'middle');
  // Clear out any past items
  scratchSVG.selectAll('*').remove();
  // Measure an SVG text element, placed at the hidden canvas' midpoint.
  const textNode = scratchSVG
    .append('text')
    .attr('x', canvas.width / 2)
    .attr('y', canvas.height / 2)
    .attr('font-weight', renderConfig.font.weight.bold)
    .text(text)
    .node();
  // Instead of failing on missing textNode, return a dummy bounding box.
  return textNode ? textNode.getBBox() : { width: 0, height: 0 };
}
